<script>
  // Super-user test chat: play a guest at a real property and see what the
  // auto-responder would decide and draft. Nothing is sent to anyone — the
  // server runs str_autoresponder/simulator.py, which never sends, notifies,
  // or writes pipeline data. The thread lives only in this page.
  import api from '$lib/api.js';
  import { authStore } from '../../stores/authStore.svelte.js';
  import PageHeader from '../../components/PageHeader.svelte';
  import EmptyState from '../../components/EmptyState.svelte';
  import {
    outcomeMeta, historyBefore, lastGuestIndex, canRerun, totalTokens,
  } from '$lib/testChat.js';

  const MAX_CHARS = 2000;

  let properties = $state([]);
  let propertiesLoading = $state(false);
  let propertiesError = $state(null);
  let propertyId = $state('');
  let guestName = $state('Alex');
  let agreement = $state('');
  let portalUrl = $state('');
  let showBooking = $state(false);

  /** { id, kind: 'guest'|'host', body, status: 'pending'|'done'|'error', result, error } */
  let entries = $state([]);
  let composer = $state('');
  let sendAs = $state('guest');
  let nextId = 0;

  const busy = $derived(entries.some((e) => e.status === 'pending'));
  const tenantId = $derived(authStore.tenantId);

  async function loadProperties() {
    reset();
    properties = [];
    propertyId = '';
    if (!tenantId) return;
    propertiesLoading = true;
    propertiesError = null;
    try {
      const result = await api.listProperties({ tenant_id: tenantId, limit: 100 });
      properties = [...result.items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    } catch (e) {
      propertiesError = e.message;
    } finally {
      propertiesLoading = false;
    }
  }

  // Re-runs when the sidebar tenant switcher changes the tenant (same pattern as
  // Situations.svelte); switching tenant also discards the conversation.
  $effect(() => {
    tenantId;
    loadProperties();
  });

  function reset() {
    entries = [];
    composer = '';
    sendAs = 'guest';
  }

  async function runGuestTurn(index) {
    const entry = entries[index];
    entry.status = 'pending';
    entry.error = null;
    entry.result = null;
    try {
      entry.result = await api.testChatTurn({
        property_id: Number(propertyId),
        message: entry.body,
        history: historyBefore(entries, index),
        guest_name: guestName.trim() || null,
        reservation_agreement: agreement || null,
        guest_portal_url: portalUrl.trim() || null,
      }, { tenant_id: tenantId });
      entry.status = 'done';
    } catch (e) {
      entry.status = 'error';
      entry.error = e.detail ?? e.message;
    }
  }

  async function send() {
    const body = composer.trim();
    if (!body || busy || !propertyId) return;
    entries.push({ id: nextId++, kind: sendAs, body, status: 'done', result: null, error: null });
    composer = '';
    if (sendAs === 'guest') {
      await runGuestTurn(entries.length - 1);
    } else {
      sendAs = 'guest';
    }
  }

  function useDraft(text) {
    entries.push({ id: nextId++, kind: 'host', body: text, status: 'done', result: null, error: null });
    sendAs = 'guest';
  }

  function editDraft(text) {
    composer = text;
    sendAs = 'host';
  }

  function writeOwn() {
    composer = '';
    sendAs = 'host';
  }

  function rerun() {
    const i = lastGuestIndex(entries);
    if (i === entries.length - 1) runGuestTurn(i);
  }

  function undoLast() {
    if (!busy) entries.pop();
  }

  function onKeydown(event) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      send();
    }
  }

  const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
  const label = (s) => (s ? String(s).replace(/_/g, ' ') : '—');
  const toneVar = (tone) => (tone === 'muted' ? 'var(--p-text-muted)' : `var(--p-${tone})`);
  const toneBg = (tone) => (tone === 'muted' ? 'var(--p-bg-subtle)' : `var(--p-${tone}-bg)`);

  // A host reply option is offered only under the newest guest message that
  // produced a draft, and only until someone has replied to it.
  function offersHostActions(index) {
    const e = entries[index];
    return e.kind === 'guest' && e.status === 'done' && index === entries.length - 1;
  }
</script>

<PageHeader title="Test chat"
            subtitle="Play a guest at one of your properties and see what the auto-responder would do.">
  {#snippet children()}
    <button type="button" class="p-btn p-btn-quiet text-sm" onclick={reset}
            disabled={busy || entries.length === 0}>
      <i class="fas fa-rotate-left mr-1" aria-hidden="true"></i> Reset
    </button>
  {/snippet}
</PageHeader>

<p class="mb-4 rounded-lg px-3 py-2 text-xs"
   style="background-color: var(--p-info-bg); color: var(--p-info)">
  <i class="fas fa-flask mr-1" aria-hidden="true"></i>
  Test mode — nothing here is sent to guests or hosts, and nothing is saved. The conversation
  disappears when you leave the page.
</p>

{#if !tenantId}
  <EmptyState icon="fa-building" title="Select a tenant first"
              message="Pick a tenant in the sidebar to choose one of its properties." />
{:else}
  <div class="p-card mb-4 grid gap-3 p-4 sm:grid-cols-3">
    <label class="text-xs font-medium sm:col-span-2">
      Property
      <select class="p-input mt-1 w-full text-sm" bind:value={propertyId}
              onchange={reset} disabled={propertiesLoading || busy}>
        <option value="">{propertiesLoading ? 'Loading…' : 'Choose a property'}</option>
        {#each properties as p (p.id)}
          <option value={String(p.id)}>
            {p.name ?? `Property #${p.id}`}{p.knowledge_item_count != null
              ? ` · ${p.knowledge_item_count} knowledge items` : ''}
          </option>
        {/each}
      </select>
    </label>
    <label class="text-xs font-medium">
      Guest name
      <input class="p-input mt-1 w-full text-sm" type="text" maxlength="100"
             bind:value={guestName} disabled={busy} />
    </label>

    <div class="sm:col-span-3">
      <button type="button" class="p-btn p-btn-quiet px-0 text-xs"
              onclick={() => (showBooking = !showBooking)}>
        <i class="fas {showBooking ? 'fa-chevron-down' : 'fa-chevron-right'} mr-1" aria-hidden="true"></i>
        Booking details (optional)
      </button>
      {#if showBooking}
        <div class="mt-2 grid gap-3 sm:grid-cols-2">
          <label class="text-xs font-medium">
            Rental agreement
            <select class="p-input mt-1 w-full text-sm" bind:value={agreement} disabled={busy}>
              <option value="">Unknown</option>
              <option value="signed">Signed</option>
              <option value="not_signed">Not signed</option>
              <option value="not_required">Not required</option>
            </select>
          </label>
          <label class="text-xs font-medium">
            Guest portal link
            <input class="p-input mt-1 w-full text-sm" type="url" maxlength="500"
                   placeholder="https://…" bind:value={portalUrl} disabled={busy} />
          </label>
          <p class="text-xs p-faint sm:col-span-2">
            Only used when the guest asks about their rental agreement. Stay dates aren't used
            when drafting, so they aren't offered here.
          </p>
        </div>
      {/if}
    </div>
    {#if propertiesError}
      <p class="text-sm sm:col-span-3" style="color: var(--p-danger)">{propertiesError}</p>
    {/if}
  </div>

  {#if !propertyId}
    <EmptyState icon="fa-comments" title="Choose a property to start"
                message="Then write a message as the guest would." />
  {:else}
    <div class="space-y-4">
      {#each entries as entry, index (entry.id)}
        {@const isGuest = entry.kind === 'guest'}
        <div class="flex" class:justify-end={!isGuest}>
          <div class="max-w-[75%]">
            <div class="p-card p-3"
                 style={isGuest ? '' : 'background-color: var(--p-brand-light)'}>
              <p class="whitespace-pre-wrap text-sm">{entry.body}</p>
            </div>
            <p class="mt-1 text-xs p-faint" class:text-right={!isGuest}>
              {isGuest ? (guestName.trim() || 'Guest') : 'Host'}
            </p>
          </div>
        </div>

        {#if isGuest}
          <div class="ml-6 border-l-2 pl-4" style="border-color: var(--p-border-strong)">
            {#if entry.status === 'pending'}
              <p class="text-sm p-muted">
                <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
                Running the pipeline… this usually takes 5–15 seconds.
              </p>
            {:else if entry.status === 'error'}
              <p class="text-sm" style="color: var(--p-danger)">
                <i class="fas fa-circle-exclamation mr-1" aria-hidden="true"></i>{entry.error}
              </p>
            {:else if entry.result}
              {@const r = entry.result}
              {@const meta = outcomeMeta(r.outcome)}
              <div class="flex flex-wrap items-center gap-2 text-xs">
                <span class="rounded-full px-2 py-0.5 font-medium"
                      style="background-color: {toneBg(meta.tone)}; color: {toneVar(meta.tone)}">
                  {meta.label}
                </span>
                {#if r.confidence?.overall != null}
                  <span class="p-muted">confidence {pct(r.confidence.overall)}</span>
                {/if}
                {#if r.trigger_reason}<span class="p-muted">· {label(r.trigger_reason)}</span>{/if}
                {#if r.urgency_level}<span class="p-muted">· urgency {r.urgency_level}</span>{/if}
              </div>

              {#if r.draft_text}
                <p class="mt-2 whitespace-pre-wrap text-sm">{r.draft_text}</p>
              {:else if r.draft_unavailable_reason}
                <p class="mt-2 text-sm p-faint">No draft — {r.draft_unavailable_reason}.</p>
              {/if}

              {#each r.notes ?? [] as note}
                <p class="mt-1 text-xs p-muted">
                  <i class="fas fa-circle-info mr-1" aria-hidden="true"></i>{note}
                </p>
              {/each}

              {#if offersHostActions(index)}
                <div class="mt-3 flex flex-wrap gap-2">
                  {#if r.draft_text}
                    <button type="button" class="p-btn p-btn-primary px-2 py-1 text-xs"
                            onclick={() => useDraft(r.draft_text)}>Use as host reply</button>
                    <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                            onclick={() => editDraft(r.draft_text)}>Edit &amp; use</button>
                  {/if}
                  <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                          onclick={writeOwn}>Write my own</button>
                  <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                          onclick={rerun} disabled={!canRerun(entries)}>
                    <i class="fas fa-rotate mr-1" aria-hidden="true"></i>Re-run
                  </button>
                </div>
              {/if}

              <details class="mt-3 text-xs">
                <summary class="cursor-pointer p-muted">Details</summary>
                <div class="mt-2 space-y-3">
                  <div>
                    <p class="font-medium">Decision</p>
                    <p class="p-muted">
                      action {label(r.recommended_action)}
                      · response type {label(r.response_type)}
                      {#if r.flags?.severity}· severity {r.flags.severity}{/if}
                      {#if r.flags?.maintenance_issue}· maintenance issue{/if}
                      {#if r.flags?.lock_issue}· lock issue{/if}
                      {#if r.flags?.closer_mode}· closer: {r.flags.closer_mode}{/if}
                    </p>
                    {#if r.production_gate}
                      <p class="p-muted">
                        production gate: {r.production_gate.allowed ? 'would pass' : 'would hold'}
                        ({r.production_gate.reason})
                      </p>
                    {/if}
                  </div>

                  {#if r.confidence}
                    <div>
                      <p class="font-medium">Confidence {pct(r.confidence.overall)}
                        {#if r.confidence.level}({label(r.confidence.level)}){/if}</p>
                      <ul class="p-muted">
                        {#each Object.entries(r.confidence.components ?? {}) as [k, v]}
                          <li>{label(k)}: {pct(v)}</li>
                        {/each}
                      </ul>
                      {#if r.confidence.notes}<p class="mt-1 p-faint">{r.confidence.notes}</p>{/if}
                    </div>
                  {/if}

                  {#if r.flags?.message_intent}
                    <div>
                      <p class="font-medium">Message intent</p>
                      <p class="p-muted">
                        {label(r.flags.message_intent.intent)}
                        · needs reply {r.flags.message_intent.needs_reply ? 'yes' : 'no'}
                        {#if r.flags.message_intent.reason}— {r.flags.message_intent.reason}{/if}
                      </p>
                    </div>
                  {/if}

                  {#if r.flags?.matched_situation}
                    <div>
                      <p class="font-medium">Saved reply matched</p>
                      <p class="p-muted">{r.flags.matched_situation.name}</p>
                      {#if r.flags.matched_situation.variant_text}
                        <p class="p-faint">“{r.flags.matched_situation.variant_text}”</p>
                      {/if}
                    </div>
                  {/if}

                  <div>
                    <p class="font-medium">Property knowledge used ({r.retrieved_knowledge?.length ?? 0})</p>
                    {#each r.retrieved_knowledge ?? [] as k}
                      <div class="mt-1 rounded p-2" style="background-color: var(--p-bg-subtle)">
                        <p class="font-medium">{k.title ?? label(k.knowledge_type)}
                          {#if k.similarity_score != null}
                            <span class="p-faint">· similarity {pct(k.similarity_score)}</span>
                          {/if}
                        </p>
                        <p class="whitespace-pre-wrap p-muted">{k.content}</p>
                      </div>
                    {:else}
                      <p class="p-faint">None — nothing in the knowledge base was judged relevant.</p>
                    {/each}
                  </div>

                  {#if r.llm_usage}
                    <p class="p-faint">
                      {r.llm_usage.calls?.length ?? 0} model calls · {totalTokens(r.llm_usage)} tokens
                      {#if r.draft_model}· drafted with {r.draft_model}{/if}
                    </p>
                  {/if}
                </div>
              </details>
            {/if}

            {#if entry.status === 'error' && index === entries.length - 1}
              <button type="button" class="p-btn p-btn-quiet mt-2 px-2 py-1 text-xs" onclick={rerun}>
                <i class="fas fa-rotate mr-1" aria-hidden="true"></i>Try again
              </button>
            {/if}
          </div>
        {/if}
      {/each}
    </div>

    <div class="p-card mt-6 p-3">
      <div class="mb-2 flex items-center gap-2 text-xs">
        <span class="p-muted">Send as</span>
        <button type="button" class="p-btn px-2 py-1 text-xs"
                class:p-btn-primary={sendAs === 'guest'} class:p-btn-quiet={sendAs !== 'guest'}
                onclick={() => (sendAs = 'guest')}>Guest</button>
        <button type="button" class="p-btn px-2 py-1 text-xs"
                class:p-btn-primary={sendAs === 'host'} class:p-btn-quiet={sendAs !== 'host'}
                onclick={() => (sendAs = 'host')}>Host</button>
        {#if entries.length}
          <button type="button" class="p-btn p-btn-quiet ml-auto px-2 py-1 text-xs"
                  onclick={undoLast} disabled={busy}>Undo last</button>
        {/if}
      </div>
      <textarea class="p-input w-full text-sm" rows="3" maxlength={MAX_CHARS}
                placeholder={sendAs === 'guest'
                  ? 'Write what the guest says…'
                  : 'Write the host’s reply (it becomes conversation history)…'}
                bind:value={composer} onkeydown={onKeydown} disabled={busy}></textarea>
      <div class="mt-2 flex items-center justify-between">
        <span class="text-xs p-faint">{composer.length}/{MAX_CHARS} · ⌘/Ctrl+Enter to send</span>
        <button type="button" class="p-btn p-btn-primary text-sm" onclick={send}
                disabled={busy || !composer.trim()}>
          {sendAs === 'guest' ? 'Send as guest' : 'Add host reply'}
        </button>
      </div>
    </div>
  {/if}
{/if}
